import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Overline } from '@/components/atoms/Eyebrow';
import { FormDateField } from '@/components/organisms/FishGroupForms';
import { formatDateShortLocalized } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import { isBeforeDay, isFutureDay } from '../../helpers/siteActions/actionDate';

import styles from './SiteActionDialog.module.scss';

interface EventDateFieldProps<TFieldValues extends FieldValues> {
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    /** From `earliestEventDay`. */
    earliest: Date;
}

/** "Event date": today or earlier, and no earlier than `earliest`, each refused with a reason. */
export default function EventDateField<TFieldValues extends FieldValues>(
    props: EventDateFieldProps<TFieldValues>
) {
    const { control, name, earliest } = props;
    const { t, i18n } = useTranslation('fishGroups');

    return (
        <section className={styles.section}>
            <Overline as="h3" className={styles.sectionLabel}>
                {t('siteActions.form.eventDate')}
            </Overline>
            <div className={styles.dateField}>
                <FormDateField
                    control={control}
                    name={name}
                    label={t('siteActions.form.date')}
                    blockFutureDates
                    blockDaysBefore={earliest}
                    rules={{
                        required: true,
                        validate: (value: unknown) => {
                            if (!(value instanceof Date)) return true;
                            if (isFutureDay(value)) return t('siteActions.form.dateInFuture');
                            if (isBeforeDay(value, earliest)) {
                                return t('siteActions.form.dateTooEarly', {
                                    date: formatDateShortLocalized(
                                        earliest,
                                        parseLocale(i18n.language)
                                    ),
                                });
                            }
                            return true;
                        },
                    }}
                />
                <p className={styles.hint}>{t('siteActions.form.dateHint')}</p>
            </div>
        </section>
    );
}
